const DEFAULT_DEBOUNCE_DELAY = 800

function requireFunction(value, name) {
  if (typeof value !== 'function') {
    throw new TypeError(`${name} must be a function.`)
  }
}

function requirePayload(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new TypeError('Draft payload must be an object.')
  }
}

/**
 * Manage draft save revisions independently from a UI framework.
 * @param {Object} options - State machine options.
 * @param {() => number} options.getExpectedVersion - Gets the current phase version.
 * @param {() => string} options.createClientRequestId - Creates an idempotency key.
 * @param {(payload: Object) => Promise<Object>} options.save - Persists one draft payload.
 * @param {(state: Object) => void} [options.onStateChange] - State update callback.
 * @param {(report: Object, operation: Object) => void|Promise<void>} [options.onSaved] - Save callback.
 * @param {(error: Error, operation: Object) => void|Promise<void>} [options.onConflict] - Conflict callback.
 * @param {(error: Error) => boolean} [options.isVersionConflict] - Conflict predicate.
 * @param {(callback: Function, delay: number) => unknown} [options.schedule] - Debounce scheduler.
 * @param {(timer: unknown) => void} [options.cancelSchedule] - Debounce cancellation.
 * @param {number} [options.debounceDelay=800] - Auto-save debounce delay.
 * @returns {Object} Draft save controls.
 */
export function createWorkReportDraftStateMachine({
  getExpectedVersion,
  createClientRequestId,
  save,
  onStateChange,
  onSaved,
  onConflict,
  isVersionConflict = () => false,
  schedule = setTimeout,
  cancelSchedule = clearTimeout,
  debounceDelay = DEFAULT_DEBOUNCE_DELAY
} = {}) {
  requireFunction(getExpectedVersion, 'getExpectedVersion')
  requireFunction(createClientRequestId, 'createClientRequestId')
  requireFunction(save, 'save')

  let revision = 0
  let currentDraft = null
  let activeSave = null
  let autoSaveTimer = null
  let lastSaveStatus = 'idle'
  let disposed = false

  function getState() {
    return {
      hasUnsavedChanges: Boolean(currentDraft),
      saveStatus: currentDraft?.saveStatus || lastSaveStatus
    }
  }

  function notifyStateChange() {
    if (!disposed && typeof onStateChange === 'function') {
      onStateChange(getState())
    }
  }

  function clearAutoSaveTimer() {
    if (autoSaveTimer !== null) {
      cancelSchedule(autoSaveTimer)
      autoSaveTimer = null
    }
  }

  function scheduleCurrentDraft() {
    if (disposed || !currentDraft || currentDraft.autoSaveStopped) {
      return
    }

    clearAutoSaveTimer()
    const scheduledRevision = currentDraft.revision
    autoSaveTimer = schedule(() => {
      autoSaveTimer = null
      if (!disposed && currentDraft?.revision === scheduledRevision) {
        const autoSave = startSave(currentDraft)
        if (autoSave) {
          autoSave.catch(() => {})
        }
      }
    }, debounceDelay)
  }

  function createSavePayload(draft) {
    if (!draft.clientRequestId) {
      draft.clientRequestId = createClientRequestId()
    }

    if (draft.expectedVersion === undefined) {
      draft.expectedVersion = getExpectedVersion()
    }

    return {
      ...draft.payload,
      expectedVersion: draft.expectedVersion,
      clientRequestId: draft.clientRequestId
    }
  }

  function continueNewerDraft(operation) {
    if (disposed
      || !currentDraft
      || currentDraft.revision <= operation.draft.revision
      || !currentDraft.waitingForActiveSave
      || currentDraft.autoSaveStopped) {
      return
    }

    currentDraft.waitingForActiveSave = false
    const autoSave = startSave(currentDraft)
    if (autoSave) {
      autoSave.catch(() => {})
    }
  }

  function startSave(draft, { allowStoppedAutoSave = false } = {}) {
    if (disposed || !draft || draft !== currentDraft) {
      return null
    }

    if (draft.autoSaveStopped && !allowStoppedAutoSave) {
      return null
    }

    if (activeSave) {
      if (activeSave.draft === draft) {
        return activeSave.promise
      }

      draft.waitingForActiveSave = true
      return activeSave.promise
    }

    const payload = createSavePayload(draft)
    const operation = { draft, payload, revision: draft.revision }
    draft.saveStatus = 'saving'
    notifyStateChange()

    const savePromise = Promise.resolve()
      .then(() => {
        if (disposed || activeSave !== operation) {
          return undefined
        }

        return save(payload)
      })
      .then(
        async (savedReport) => {
          if (disposed) {
            return savedReport
          }

          if (typeof onSaved === 'function') {
            await onSaved(savedReport, operation)
          }

          if (!disposed && currentDraft?.revision === operation.revision) {
            currentDraft = null
            lastSaveStatus = 'saved'
            notifyStateChange()
          }

          return savedReport
        },
        async (error) => {
          if (disposed) {
            throw error
          }

          const conflict = isVersionConflict(error)
          if (currentDraft?.revision === operation.revision) {
            draft.saveStatus = conflict ? 'conflict' : 'error'
            draft.autoSaveStopped = conflict
            notifyStateChange()
            if (conflict && typeof onConflict === 'function') {
              await onConflict(error, operation)
            }
          }

          throw error
        }
      )
      .finally(() => {
        if (!disposed && activeSave?.revision === operation.revision) {
          activeSave = null
          continueNewerDraft(operation)
        }
      })

    operation.promise = savePromise
    activeSave = operation
    return savePromise
  }

  function markDirty(payload) {
    if (disposed) {
      return
    }

    requirePayload(payload)
    currentDraft = {
      revision: ++revision,
      payload: { ...payload },
      expectedVersion: undefined,
      clientRequestId: null,
      saveStatus: 'dirty',
      autoSaveStopped: false,
      waitingForActiveSave: false
    }
    lastSaveStatus = 'dirty'
    notifyStateChange()
    scheduleCurrentDraft()
  }

  function flush() {
    if (disposed) {
      return null
    }

    clearAutoSaveTimer()
    return startSave(currentDraft)
  }

  function retry() {
    if (disposed) {
      return null
    }

    clearAutoSaveTimer()
    return startSave(currentDraft, { allowStoppedAutoSave: true })
  }

  function cancelAutoSave() {
    if (disposed) {
      return
    }

    clearAutoSaveTimer()
  }

  function discard() {
    if (disposed) {
      return
    }

    clearAutoSaveTimer()
    currentDraft = null
    lastSaveStatus = 'idle'
    notifyStateChange()
  }

  function dispose() {
    if (disposed) {
      return
    }

    disposed = true
    clearAutoSaveTimer()
    currentDraft = null
    lastSaveStatus = 'idle'
  }

  return {
    markDirty,
    flush,
    retry,
    cancelAutoSave,
    discard,
    dispose,
    getState
  }
}
